import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, Users, Briefcase, UserCircle, Shield } from 'lucide-react';
import { navLinks } from '../../data/mock';
import { Button } from '../ui/button';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLoginDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loginOptions = [
    { name: 'Investor Login', path: '/investors', icon: Users, description: 'View your portfolio' },
    { name: 'Employee Login', path: '/employee/login', icon: UserCircle, description: 'Onboarding & documents' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 lg:space-x-3">
            <div className="relative">
              <div className="w-10 h-10 lg:w-14 lg:h-14 bg-gradient-to-br from-blue-600 via-teal-500 to-cyan-400 rounded-xl lg:rounded-2xl flex items-center justify-center shadow-xl transform hover:scale-105 transition-transform">
                <div className="flex flex-col items-center">
                  <span className="text-white font-extrabold text-base lg:text-xl leading-none">S</span>
                  <div className="flex space-x-0.5 mt-0.5">
                    <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-yellow-400 rounded-full"></span>
                    <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-orange-400 rounded-full"></span>
                    <span className="w-1 h-1 lg:w-1.5 lg:h-1.5 bg-pink-400 rounded-full"></span>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 lg:-bottom-1 lg:-right-1 w-4 h-4 lg:w-5 lg:h-5 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white text-[6px] lg:text-[8px] font-bold">✦</span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className={`font-extrabold text-lg lg:text-2xl tracking-tight ${
                scrolled ? 'text-gray-900' : 'text-white'
              }`}>
                STEAM<span className="text-teal-400">-IE</span>
              </span>
              <span className={`text-[8px] lg:text-[10px] font-medium tracking-widest hidden sm:block ${
                scrolled ? 'text-gray-500' : 'text-gray-200'
              }`}>
                IGNITE MINDS • INSPIRE FUTURES
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? scrolled
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-white bg-white/20'
                    : scrolled
                    ? 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    : 'text-gray-100 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Login Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                onClick={() => setShowLoginDropdown(!showLoginDropdown)}
                variant="outline"
                className={`px-4 py-2 rounded-full transition-all duration-300 flex items-center gap-2 ${
                  scrolled 
                    ? 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400' 
                    : 'border-white/50 text-white hover:bg-white/10'
                }`}
                data-testid="login-dropdown-btn"
              >
                <User className="w-4 h-4" />
                Login
                <ChevronDown className={`w-4 h-4 transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`} />
              </Button>
              
              {/* Dropdown Menu */}
              {showLoginDropdown && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50" data-testid="login-dropdown-menu">
                  <div className="p-2">
                    {loginOptions.map((option) => (
                      <Link
                        key={option.path}
                        to={option.path}
                        onClick={() => setShowLoginDropdown(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                        data-testid={`login-option-${option.name.toLowerCase().replace(' ', '-')}`}
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-teal-400 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                          <option.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{option.name}</p>
                          <p className="text-xs text-gray-500">{option.description}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/contact">
              <Button
                className="bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white px-6 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 py-4 space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                location.pathname === link.path
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
              }`}
            >
              {link.name}
            </Link>
          ))}
          
          {/* Mobile Login Options */}
          <div className="border-t pt-4 mt-4">
            <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Login As</p>
            {loginOptions.map((option) => (
              <Link
                key={option.path}
                to={option.path}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-teal-400 rounded-lg flex items-center justify-center">
                  <option.icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{option.name}</p>
                  <p className="text-xs text-gray-500">{option.description}</p>
                </div>
              </Link>
            ))}
          </div>
          
          <Link to="/contact" onClick={() => setIsOpen(false)}>
            <Button className="w-full mt-4 bg-gradient-to-r from-blue-600 to-teal-500 text-white rounded-full">
              Get Started
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
